import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'

import { useRadioGroupContext } from '..'

export type RadioProps = {
  value: boolean | string
  id?: string
  labelColor?: string
  disabled?: boolean
  className?: string
}

export const Radio = forwardRef<
  () => HTMLInputElement,
  WithChildren<RadioProps>
>(({ id, value, labelColor, disabled, className, children, ...rest }, ref) => {
  const { onChange, selectedValue } = useRadioGroupContext()

  return (
    <RadioItem
      id={id}
      checked={value === selectedValue}
      onClick={() => {
        onChange(value)
      }}
      className={className}
      labelColor={labelColor}
      disabled={disabled}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any} // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/28884
      {...rest}
    >
      {children}
    </RadioItem>
  )
})

Radio.displayName = 'Radio' // Typescript workaround for ForwardRef Component

type RadioItemProps = {
  checked?: boolean
  labelColor?: string
  disabled?: boolean
}

const RadioItem = styled.div<RadioItemProps>`
  position: relative;
  overflow: hidden;
  display: block;
  padding-left: 30px;
  cursor: pointer;
  color: ${({ labelColor, theme }) =>
    labelColor || theme.color.primari.grigioEstra};

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 3px;
    left: 0;
    border-radius: 50%;
    width: 20px;
    height: 20px;
  }

  &:before {
    background-color: ${({ theme }) => theme.color.neutri.grigioBg};
    border: 1px solid ${({ theme }) => theme.color.neutri.grigioMedio};
  }

  &:after {
    ${({ checked }) =>
      checked
        ? css`
            background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Cpath fill='%23fff' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26 2.974 7.25 8 2.193z'/%3E%3C/svg%3E");
            background-size: 10px;
            background-position: center;
            background-repeat: no-repeat;
            background-color: ${({ theme }) => theme.color.primari.verde};
          `
        : null}

  ${({ disabled }) =>
    disabled
      ? css`
          pointer-events: none;
          opacity: 0.7;
        `
      : null}
`
